import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { ApiError } from "../../api/errors";
import { getProjectRepository } from "../../data/projectRepository";
import type {
  InterventionCourse,
  ResourceRoomProjectDocumentV2,
  Room,
  Teacher
} from "../../storage/resourceRoomDocument";
import { ResourceRoomProjectHeader } from "../shared/ResourceRoomProjectHeader";
import {
  demoInterventionCourseNames,
  demoRoomNames,
  demoTeacherNames
} from "../shared/resourceRoomDemoData";
import "../shared/resource-room-workspace.css";

type Tab = "COURSES" | "TEACHERS" | "ROOMS" | "UNAVAILABILITY";
type CatalogItem = InterventionCourse | Teacher | Room;

interface Feedback {
  kind: "success" | "error";
  text: string;
}

const tabs: Array<[Tab, string]> = [
  ["COURSES", "干预课程"],
  ["TEACHERS", "教师"],
  ["ROOMS", "具体教室"],
  ["UNAVAILABILITY", "不可排时间"]
];

function CatalogSection({
  kind,
  title,
  items,
  busy,
  run
}: {
  kind: "INTERVENTION_COURSE" | "TEACHER" | "ROOM";
  title: string;
  items: CatalogItem[];
  busy: boolean;
  run: (type: string, payload: Record<string, unknown>) => void;
}): React.JSX.Element {
  const [name, setName] = useState("");
  const prefix = kind === "INTERVENTION_COURSE" ? "INTERVENTION_COURSE" : kind;
  function submit(event: FormEvent): void {
    event.preventDefault();
    run(`CREATE_${prefix}`, { name });
    setName("");
  }
  return (
    <section aria-labelledby={`${kind}-title`}>
      <h2 id={`${kind}-title`}>{title}</h2>
      <form className="resource-inline-form" onSubmit={submit}>
        <label className="field"><span>名称</span><input required value={name} onChange={(event) => setName(event.target.value)} /></label>
        <button className="primary-button" disabled={busy} type="submit">新增</button>
      </form>
      {items.length ? (
        <div className="resource-table-scroll">
          <table className="data-table">
            <thead><tr><th>名称</th><th>操作</th></tr></thead>
            <tbody>{items.map((item) => {
              return (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td><div className="resource-actions">
                    <button className="text-button" type="button" disabled={busy} onClick={() => { const next = window.prompt(`修改“${item.name}”的名称`, item.name); if (next) run(`UPDATE_${prefix}`, { id: item.id, name: next }); }}>改名</button>
                    <button className="text-button danger" type="button" disabled={busy} onClick={() => { if (window.confirm(`确认删除“${item.name}”？`)) run(`DELETE_${prefix}`, { id: item.id }); }}>删除</button>
                  </div></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      ) : <div className="empty-card compact"><h2>尚无{title}</h2></div>}
    </section>
  );
}

function UnavailabilitySection({
  document,
  busy,
  run
}: {
  document: ResourceRoomProjectDocumentV2;
  busy: boolean;
  run: (type: string, payload: Record<string, unknown>) => void;
}): React.JSX.Element {
  const [scope, setScope] = useState<"SCHOOL" | "TEACHER" | "ROOM">("SCHOOL");
  const [targetId, setTargetId] = useState("");
  const [weekday, setWeekday] = useState(1);
  const [slotId, setSlotId] = useState(document.entities.time_slots[0]?.id ?? "");
  const targets = scope === "TEACHER" ? document.entities.teachers : scope === "ROOM" ? document.entities.rooms : [];
  const targetNames = new Map([...document.entities.teachers, ...document.entities.rooms].map((item) => [item.id, item.name]));
  const slotNames = new Map(document.entities.time_slots.map((item) => [item.id, item.raw_label]));
  function submit(event: FormEvent): void {
    event.preventDefault();
    run("SET_UNAVAILABILITY", { scope, target_id: scope === "SCHOOL" ? null : targetId, weekday, time_slot_id: slotId, unavailable: true });
  }
  return (
    <section aria-labelledby="unavailability-title">
      <h2 id="unavailability-title">不可排时间</h2>
      <p>全校资源服务、单个教师和单个教室不可排均为硬约束；已有安排的格子不会被覆盖。</p>
      {!document.entities.time_slots.length ? <div className="resource-errors">请先导入原班课表，建立统一节次。</div> : (
        <form className="resource-inline-form" onSubmit={submit}>
          <label className="field"><span>范围</span><select value={scope} onChange={(event) => { const next = event.target.value as typeof scope; setScope(next); setTargetId(""); }}><option value="SCHOOL">全校资源服务</option><option value="TEACHER">教师</option><option value="ROOM">教室</option></select></label>
          {scope !== "SCHOOL" ? <label className="field"><span>{scope === "TEACHER" ? "教师" : "教室"}</span><select required value={targetId} onChange={(event) => setTargetId(event.target.value)}><option value="">请选择</option>{targets.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label> : null}
          <label className="field"><span>星期</span><select value={weekday} onChange={(event) => setWeekday(Number(event.target.value))}>{[1, 2, 3, 4, 5].map((day) => <option key={day} value={day}>星期{"一二三四五"[day - 1]}</option>)}</select></label>
          <label className="field"><span>节次</span><select required value={slotId} onChange={(event) => setSlotId(event.target.value)}>{document.entities.time_slots.map((item) => <option key={item.id} value={item.id}>{item.raw_label}</option>)}</select></label>
          <button className="primary-button" type="submit" disabled={busy || !slotId || (scope !== "SCHOOL" && !targetId)}>添加不可排</button>
        </form>
      )}
      <div className="resource-table-scroll">
        <table className="data-table"><thead><tr><th>范围</th><th>对象</th><th>时间</th><th>操作</th></tr></thead><tbody>{document.entities.unavailability.map((item) => <tr key={item.id}><td>{item.scope === "SCHOOL" ? "全校资源服务" : item.scope === "TEACHER" ? "教师" : "教室"}</td><td>{item.target_id ? targetNames.get(item.target_id) : "全部"}</td><td>星期{"一二三四五"[item.weekday - 1]} · {slotNames.get(item.time_slot_id)}</td><td><button className="text-button danger" type="button" disabled={busy} onClick={() => run("SET_UNAVAILABILITY", { scope: item.scope, target_id: item.target_id, weekday: item.weekday, time_slot_id: item.time_slot_id, unavailable: false })}>移除</button></td></tr>)}</tbody></table>
      </div>
    </section>
  );
}

export function ResourceDataPage({ projectId }: { projectId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("COURSES");
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const documentQuery = useQuery({ queryKey: ["resource-room-document", projectId], queryFn: async () => (await getProjectRepository()).readDocument(projectId) });
  const mutation = useMutation({
    mutationFn: async ({ type, payload }: { type: string; payload: Record<string, unknown> }) => (await getProjectRepository()).execute(projectId, type, payload),
    onSuccess: async () => { setFeedback({ kind: "success", text: "资料已保存" }); await queryClient.invalidateQueries({ predicate: (item) => item.queryKey.includes(projectId) }); },
    onError: (error: unknown) => {
      let text = error instanceof Error ? error.message : "保存失败";
      if (error instanceof ApiError && error.code === "HARD_CONFLICT") {
        const planIds = Array.isArray(error.details.plan_ids)
          ? error.details.plan_ids.map(String)
          : [];
        const document = documentQuery.data;
        if (document && planIds.length) {
          const courseNames = new Map(document.entities.intervention_courses.map((item) => [item.id, item.name]));
          const studentNames = new Map(document.entities.students.map((item) => [item.id, item.display_name]));
          const affected = document.entities.intervention_plans
            .filter((plan) => planIds.includes(plan.id))
            .map((plan) => `${courseNames.get(plan.course_id) ?? "未命名课程"}（${plan.student_ids.map((id) => studentNames.get(id)).filter(Boolean).join("、")}）`);
          if (affected.length) text = `${text}。受影响计划：${affected.join("；")}`;
        }
      }
      setFeedback({ kind: "error", text });
    }
  });
  const demoMutation = useMutation({
    mutationFn: async () => {
      const repository = await getProjectRepository();
      const before = await repository.readDocument(projectId);
      const initialCount =
        before.entities.intervention_courses.length +
        before.entities.teachers.length +
        before.entities.rooms.length +
        before.entities.unavailability.length;

      for (const name of demoInterventionCourseNames) {
        const current = await repository.readDocument(projectId);
        if (!current.entities.intervention_courses.some((item) => item.name === name)) {
          await repository.execute(projectId, "CREATE_INTERVENTION_COURSE", { name });
        }
      }

      for (const name of demoTeacherNames) {
        const current = await repository.readDocument(projectId);
        if (!current.entities.teachers.some((item) => item.name === name)) {
          await repository.execute(projectId, "CREATE_TEACHER", { name });
        }
      }

      for (const name of demoRoomNames) {
        const current = await repository.readDocument(projectId);
        if (!current.entities.rooms.some((item) => item.name === name)) {
          await repository.execute(projectId, "CREATE_ROOM", { name });
        }
      }

      const current = await repository.readDocument(projectId);
      if (!current.entities.intervention_plans.length && current.entities.time_slots.length) {
        const slots = [...current.entities.time_slots].sort((left, right) => left.sort_order - right.sort_order);
        const teacher = current.entities.teachers[0];
        const resourceRoom = current.entities.rooms.find((item) => item.name === demoRoomNames[0]);
        const examples = [
          { scope: "SCHOOL", target_id: null, weekday: 5, time_slot_id: slots.at(-1)?.id },
          { scope: "TEACHER", target_id: teacher?.id, weekday: 3, time_slot_id: slots[0]?.id },
          { scope: "ROOM", target_id: resourceRoom?.id, weekday: 2, time_slot_id: slots.at(-1)?.id }
        ] as const;
        for (const example of examples) {
          if (!example.time_slot_id || (example.scope !== "SCHOOL" && !example.target_id)) continue;
          await repository.execute(projectId, "SET_UNAVAILABILITY", {
            ...example,
            unavailable: true
          });
        }
      }

      const after = await repository.readDocument(projectId);
      const finalCount =
        after.entities.intervention_courses.length +
        after.entities.teachers.length +
        after.entities.rooms.length +
        after.entities.unavailability.length;
      return Math.max(0, finalCount - initialCount);
    },
    onSuccess: async (addedCount) => {
      setFeedback({
        kind: "success",
        text: addedCount
          ? `已补充 ${addedCount} 项模拟资源资料；已有资料保持不变。`
          : "模拟资源资料已齐全，没有改动已有内容。"
      });
      await queryClient.invalidateQueries({ predicate: (item) => item.queryKey.includes(projectId) });
    },
    onError: (error: unknown) => setFeedback({
      kind: "error",
      text: error instanceof Error ? error.message : "模拟资源资料填写失败"
    })
  });
  if (!documentQuery.data) return <main className="page-shell"><p className="state-message">正在读取资源资料…</p></main>;
  const document = documentQuery.data;
  const busy = mutation.isPending || demoMutation.isPending;
  const run = (type: string, payload: Record<string, unknown>) => mutation.mutate({ type, payload });
  return (
    <main className="page-shell">
      <ResourceRoomProjectHeader
        projectId={projectId}
        eyebrow="阶段 2 · 资源资料"
        title="资源资料"
        actions={(
          <button
            className="secondary-button demo-data-button"
            type="button"
            disabled={busy}
            onClick={() => demoMutation.mutate()}
          >
            {demoMutation.isPending ? "正在补充…" : "补充模拟资源资料"}
          </button>
        )}
      />
      <section className="resource-panel">
        <nav className="resource-tabs" aria-label="资源资料分类">{tabs.map(([value, label]) => <button className={tab === value ? "active" : ""} key={value} type="button" onClick={() => setTab(value)}>{label}</button>)}</nav>
        {feedback ? <p className={feedback.kind === "success" ? "resource-success" : "resource-errors"} role={feedback.kind === "error" ? "alert" : "status"}>{feedback.text}</p> : null}
        {tab === "COURSES" ? <CatalogSection kind="INTERVENTION_COURSE" title="干预课程" items={document.entities.intervention_courses} busy={busy} run={run} /> : null}
        {tab === "TEACHERS" ? <CatalogSection kind="TEACHER" title="教师" items={document.entities.teachers} busy={busy} run={run} /> : null}
        {tab === "ROOMS" ? <CatalogSection kind="ROOM" title="具体教室" items={document.entities.rooms} busy={busy} run={run} /> : null}
        {tab === "UNAVAILABILITY" ? <UnavailabilitySection document={document} busy={busy} run={run} /> : null}
      </section>
    </main>
  );
}
